import * as React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import compact from 'lodash/compact'
import xor from 'lodash/xor'
import moment from 'moment'

import { observer } from 'mobx-react'

import EmployeeSelector from 'modules/core/components/EmployeeSelector'
import DayPicker from 'modules/core/components/Inputs/DayPicker'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { dateToString, dateUIToBackend } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import employees from 'modules/employees/store/collections/employees'
import PlanningVersionsApi from 'modules/time_planning/store/collections/planningVersions'
import { PLANNING_TOOL } from 'modules/time_planning/store/models/PlanningVersion'
import { selectableEmployeesByCurrent } from 'modules/work_schedules/lib/selectableEmployeesByCurrent'
import type WorkSchedule from 'modules/work_schedules/store/models/Schedule'

const AddEmployeesModal = ({
  workSchedule,
  onClose,
  onSuccess,
  contractVersions,
}: {
  workSchedule: WorkSchedule
  onClose: () => void
  onSuccess: () => void
  contractVersions: ContractVersions
}) => {
  const [selectedEmployees, setEmployees] = React.useState<number[]>([])

  const setEmployee = (id: number | null | undefined) =>
    id ? setEmployees(xor(selectedEmployees, [id])) : setEmployees([])

  // get employees already assigned to this work schedule
  const currentEmployeesIds = workSchedule.employeeIds

  // get assignable employees that are not assigned to this work schedule
  // and pass them to the modal

  const selectableEmployees = selectableEmployeesByCurrent(contractVersions)

  const filterModalEmployees = compact(
    selectableEmployees
      .filter((e) => !currentEmployeesIds.includes(e.attributes.id))
      .map((employee) => employees.get(employee.id))
  )

  const form = useLazyRef<Form>(
    () =>
      new Form(
        {
          effective_at: dateToString(moment(), 'backend'),
        },
        {
          effective_at: 'date',
        }
      )
  ).current

  const save = async () => {
    const effectiveAt = dateUIToBackend(form.get('effective_at').value)

    const success = await PlanningVersionsApi.rpc('bulk_create', {
      employee_ids: selectedEmployees,
      effective_at: effectiveAt,
      planning_tool: PLANNING_TOOL.workSchedule,
      schedule_id: workSchedule.attributes.id,
    })

    if (success) {
      selectedEmployees.map((id) => {
        const employee = employees.get(id)
        employee?.set({
          effective_at: effectiveAt,
        })
        employee?.update()
      })
    }
  }

  const { running, performAction: onSubmit } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      await Promise.all([save()])

      onSuccess()
      closePortal()
      onClose()
    }
  )

  return (
    <Modal
      illustration="calendar-clock"
      title={i18n.t('settings.work_schedule.employees_tab.employee_modal.assign')}
      description={i18n.t('settings.work_schedule.employees_tab.employee_modal.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('settings.work_schedule.cancel')}
          </Button>
          <Button disabled={running} submit>
            {i18n.t('settings.work_schedule.employees_tab.employee_modal.assign')}
          </Button>
        </>
      }
    >
      <EmployeeSelector
        employees={filterModalEmployees}
        onSelectEmployee={setEmployee}
        onSelectMultiple={setEmployees}
        selectedEmployeeIds={selectedEmployees}
        contractVersions={contractVersions}
      />
      <Box paddingY="s16">
        <DayPicker
          field={form.get('effective_at')}
          label={i18n.t('settings.work_schedule.employees_tab.table.header.date')}
        />
      </Box>
      <Text.Normal>
        {i18n.t('settings.work_schedule.employees_tab.employee_modal.footer_description')}
      </Text.Normal>
    </Modal>
  )
}

export default observer(AddEmployeesModal)
